import SubHeaders from '@/components/Headers/SubHeaders'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { generatePathDatacentral, paths } from '@/utils/path'
import dayjs from 'dayjs'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import { getHolidayById, updateHoliday } from '../../services'
import { HOLIDAY_OPTIONS, WORK_N_DAY_TYPES } from '../../types/utils'
import { holidayUpdateSchema } from '../Create/schema'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const HolidayUpdate: React.FC = () => {
    const { holidayID } = useParams<{ holidayID: string }>()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const { data: holiday, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.HOLIDAY_DETAIL],
        queryFn: async () => {
            const result = await getHolidayById({
                holiday_id: Number(holidayID) || 0,
            })
            return result
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: async (data: any) => {
                await updateHoliday({
                    holiday_id: Number(holidayID),
                    holiday_name: data.holiday_name,
                    holiday_date: dayjs(data.holiday_date)
                        .startOf('day')
                        .format('YYYY-MM-DDTHH:mm:ssZ'),
                    holiday_work_n_day: data.holiday_work_n_day,
                })
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.HOLIDAYS,
            successMessage: 'Update Holiday Successfully',
            errorMessage: 'something went wrong',
            onSuccess: () => {
                navigate(generatePathDatacentral(paths.workdayHoliday, {}), {
                    replace: true,
                })
            },
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div>
            <SubHeaders
                actions={[]}
                breadcrumbs={[
                    {
                        path: paths.workdayHoliday,
                        title: t('holiday-list'),
                    },
                    {
                        path: `${paths.workdayHoliday}/${holiday?.result?.holiday_id}`,
                        title: t('holiday-update'),
                    },
                ]}
            />
            <div>
                <GridInformationWithHookForm
                    loading={isPending}
                    defaultFormValues={{
                        holiday_name: holiday?.result?.holiday_name || '',
                        holiday_date: dayjs(
                            holiday?.result?.holiday_date
                        ).format('YYYY-MM-DD'),
                        holiday_work_n_day:
                            holiday?.result?.holiday_work_n_day || 2,
                    }}
                    onEdit={mutateAsync}
                    schema={holidayUpdateSchema as any}
                    data={[
                        {
                            label: t('holiday-date'),
                            value: holiday?.result?.holiday_date
                                ? dayjs(holiday?.result?.holiday_date).format(
                                      'DD/MM/YYYY'
                                  )
                                : null,
                            formItemProps: {
                                name: 'holiday_date',
                                type: 'datepicker',
                            },
                        },
                        {
                            label: t('holiday-work-n-day'),
                            value: WORK_N_DAY_TYPES(t).find(
                                (item) =>
                                    item.value ===
                                    holiday?.result?.holiday_work_n_day
                            )?.label,
                            formItemProps: {
                                name: 'holiday_work_n_day',
                                direction: 'row',
                                type: 'radiogroup',
                                options: WORK_N_DAY_TYPES(t),
                            },
                            className: 'col-span-2',
                        },
                        {
                            label: t('holiday-name'),
                            value: HOLIDAY_OPTIONS(t).find(
                                (item) =>
                                    item.value === holiday?.result?.holiday_name
                            )?.label,
                            formItemProps: {
                                name: 'holiday_name',
                                type: 'select',
                                options: HOLIDAY_OPTIONS(t),
                            },
                        },
                    ]}
                    head={{
                        title: `${t('holiday-update')}${
                            holiday?.result?.holiday_id
                                ? ` #${holiday.result.holiday_id}`
                                : ''
                        }`,
                    }}
                />
            </div>
        </div>
    )
}

export default HolidayUpdate
